import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";
import { useLongPress } from "../../hooks/use-long-press";
import { iosGenerateHaptic } from "../../tools/ios";
import { HapticEffect } from "../../interfaces";
import { POPOVER_CARD_CLASS } from "./Popover";

// Least room kept between the card and a viewport edge, and gap between the
// card and the message, in CSS pixels. Numbers rather than classes: the card
// is placed by measurement.
const EDGE_PX = 8;
const GAP_PX = 8;

type LongPressMenuItem = {
  label: string;
  icon: React.ReactNode;
  onSelect: () => void;
};

/**
 * The touch context menu of a message (the one the Claude iOS app opens): a
 * long press on `children` gives a selection haptic and opens `items` under
 * them, right-aligned, or above them when the screen has no room below.
 *
 * Off the message, not at the finger: the menu opens in the same place
 * wherever the message was pressed. The wrapper is the anchor, so `className`
 * makes it hug the message.
 *
 * No backdrop. A full-screen layer would take the next swipe and scroll
 * nothing, the trap the evidence popovers fell into. Window listeners in the
 * capture phase close the menu instead: a scroll gesture closes it while the
 * page scrolls, and an outside tap closes it without reaching what it landed
 * on.
 *
 * Touch only: with a mouse the text is selectable in place and the browser's
 * own context menu applies.
 */
export function LongPressMenu({
  items,
  className,
  children,
}: {
  items: ReadonlyArray<LongPressMenuItem>;
  className: string | null;
  children: React.ReactNode;
}): React.JSX.Element {
  // The message's box when the press landed, and so where the menu opens.
  // Null = closed.
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [placement, setPlacement] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const { handlers, didLongPressRef } = useLongPress({
    onLongPress: () => {
      const element = anchorRef.current;
      if (element === null)
        throw new Error("should not happen: a long press on no message");
      iosGenerateHaptic(HapticEffect.SELECTION);
      setPlacement(null);
      setAnchor(element.getBoundingClientRect());
    },
  });

  // Measured before paint: the card renders hidden, is measured here, and
  // lands under the message, flipped above it and clamped to stay on screen.
  useLayoutEffect(() => {
    if (anchor === null) return;
    const card = cardRef.current;
    if (card === null)
      throw new Error("should not happen: an open menu is not mounted");
    const { width, height } = card.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    const top =
      anchor.bottom + GAP_PX + height + EDGE_PX <= viewportHeight
        ? anchor.bottom + GAP_PX
        : anchor.top - GAP_PX - height;
    setPlacement({
      left: Math.max(
        Math.min(anchor.right - width, viewportWidth - width - EDGE_PX),
        EDGE_PX
      ),
      top: Math.max(Math.min(top, viewportHeight - height - EDGE_PX), EDGE_PX),
    });
  }, [anchor]);

  useEffect(() => {
    if (anchor === null) return;
    function close(): void {
      setAnchor(null);
    }
    function closeOnOutsideTap(event: MouseEvent): void {
      const card = cardRef.current;
      if (card !== null && event.composedPath().includes(card)) return;
      event.preventDefault();
      event.stopPropagation();
      close();
    }
    const passive = { capture: true, passive: true };
    window.addEventListener("touchmove", close, passive);
    window.addEventListener("wheel", close, passive);
    window.addEventListener("scroll", close, passive);
    window.addEventListener("click", closeOnOutsideTap, true);
    return () => {
      window.removeEventListener("touchmove", close, passive);
      window.removeEventListener("wheel", close, passive);
      window.removeEventListener("scroll", close, passive);
      window.removeEventListener("click", closeOnOutsideTap, true);
    };
  }, [anchor]);

  return (
    <div
      ref={anchorRef}
      className={cn(
        "transition-opacity duration-150",
        // The pressed message dims while its menu is open, so the reader sees
        // which one the menu acts on.
        anchor !== null && "opacity-60",
        className
      )}
      onTouchStart={handlers.onTouchStart}
      onTouchEnd={(event) => {
        // The finger that opened the menu lifts on whatever it pressed. The
        // click that lift would synthesize is cancelled here: the outside-tap
        // listener would read it as a tap outside and close the menu it just
        // opened.
        if (didLongPressRef.current) event.preventDefault();
        handlers.onTouchEnd();
      }}
      onTouchMove={handlers.onTouchMove}
    >
      {children}
      {anchor !== null &&
        createPortal(
          <div
            ref={cardRef}
            role="menu"
            className={cn(POPOVER_CARD_CLASS, "fixed min-w-52")}
            style={
              placement === null
                ? { top: 0, left: 0, visibility: "hidden" }
                : placement
            }
          >
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                className="flex min-h-11 w-full cursor-pointer items-center gap-2.5 border-none bg-transparent px-4 text-left font-dm-sans text-ui-body font-medium text-ds-black hover:bg-ds-black/4"
                onClick={() => {
                  setAnchor(null);
                  item.onSelect();
                }}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
